import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { scrollToId } from "@/App";

const VIDEO = "/videos/rolex-hero.mp4";
const POSTER = "/images/hero-poster.jpg";

const fade = (ready, delay, y = 24) => ({
  initial: { opacity: 0, y },
  animate: ready ? { opacity: 1, y: 0 } : { opacity: 0, y },
  transition: { duration: 1.2, delay, ease: EASE },
});

export default function Hero({ ready = true }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const veil = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="accueil" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.video
        src={VIDEO}
        poster={POSTER}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        style={{ scale: videoScale }}
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="hero-video"
      />
      <motion.div style={{ opacity: veil }} className="absolute inset-0 bg-[#07070a] pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 55%, transparent 20%, rgba(7,7,10,0.55) 100%), linear-gradient(0deg, rgba(9,9,10,1) 0%, transparent 28%, transparent 80%, rgba(9,9,10,0.6) 100%)",
        }}
      />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col items-center justify-center text-center"
      >
        <motion.img
          src="/images/logo.png"
          alt="BABYLON"
          {...fade(ready, 0.2, 12)}
          className="h-12 sm:h-16 lg:h-20 w-auto drop-shadow-[0_0_40px_rgba(197,160,89,0.25)]"
          data-testid="hero-logo"
        />
        <motion.p
          {...fade(ready, 0.55, 10)}
          className="mt-8 font-mono text-[10px] sm:text-xs tracking-[0.55em] uppercase text-gold"
        >
          Conciergerie horlogère privée
        </motion.p>
        <motion.h1
          {...fade(ready, 0.8)}
          className="mt-10 font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-ivory max-w-4xl [text-shadow:0_2px_40px_rgba(0,0,0,0.8)]"
          data-testid="hero-title"
        >
          Vous recherchez. <span className="italic text-gold">Nous trouvons.</span>
        </motion.h1>
        <motion.p
          {...fade(ready, 1.05)}
          className="mt-8 max-w-xl text-sm sm:text-base lg:text-lg text-ivory/70 leading-relaxed"
          data-testid="hero-description"
        >
          Recherche et acquisition de garde-temps d'exception, en toute discrétion.
        </motion.p>
        <motion.div {...fade(ready, 1.3)} className="mt-12">
          <button
            onClick={() => scrollToId("demande")}
            className="group inline-flex items-center gap-5 border border-gold/60 bg-obsidian/30 backdrop-blur-sm text-gold px-9 py-4 text-[11px] sm:text-[12px] tracking-[0.3em] uppercase hover:bg-gold hover:text-obsidian transition-all duration-700"
            data-testid="hero-cta-primary"
          >
            Confier ma recherche
            <span className="h-px w-8 bg-current transition-all duration-500 group-hover:w-12" />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: ready ? 1 : 0 }}
        transition={{ delay: 2, duration: 1 }}
        onClick={() => scrollToId("babylon")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ivory/50 hover:text-gold transition-colors flex flex-col items-center gap-3"
        aria-label="Défiler vers le bas"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-mono text-[9px] tracking-[0.45em] uppercase">Découvrir</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={14} strokeWidth={1.2} />
        </motion.span>
      </motion.button>
    </section>
  );
}
